import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const [settings, setSettings] = useState(null);

  // Fetch global settings to see if theme is forced or has a different default
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(getApiUrl('/api/settings'));
        const config = response.data.data;
        setSettings(config);

        const savedTheme = localStorage.getItem('theme');
        
        // If theme switching is DISABLED, force the admin's choice
        if (config.allowThemeSwitch === false) {
          setIsDarkMode(config.defaultTheme === 'dark');
        } 
        // If it's a first-time visitor (no saved theme), use admin's default
        else if (!savedTheme && config.defaultTheme) {
          setIsDarkMode(config.defaultTheme === 'dark');
        }
      } catch (error) {
        console.error('Failed to fetch theme settings:', error);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    if (isDarkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    // Only allow toggle if admin permits it
    if (settings?.allowThemeSwitch === false) {
      console.log('Theme switching is disabled by admin.');
      return;
    }
    setIsDarkMode((prev) => !prev);
  };

  return (
    <ThemeContext.Provider value={{ 
      isDarkMode, 
      toggleTheme, 
      allowThemeSwitch: settings?.allowThemeSwitch ?? true,
      defaultTheme: settings?.defaultTheme ?? 'dark'
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
